/**
 * Where everything on the move has got to.
 *
 * A transfer says a tool is going from A to B. A journey says which places it
 * passes on the way, and how far along it is - which is the question asked when
 * somebody rings up to ask where their mould is.
 *
 * The board answers it without opening anything: the site it left, the site it
 * is headed for, and whether it is standing somewhere or on the road between
 * two places. Opening a row shows the whole line with times against each stop.
 *
 * Online-only, like transfers. A journey is a live claim about right now, and a
 * stale local copy of that is worse than none.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, MapPin, Plus, Route as RouteIcon, Search, Truck, X } from 'lucide-react';
import {
  ApiClientError,
  JOURNEY_STATUS_LABEL,
  type JourneyRow,
  type JourneyStatus,
} from '@mouldtrack/shared';
import { Page } from '../../components/layout/Page.js';
import { DataTable, type Column } from '../../components/data/DataTable.js';
import { Button } from '../../components/ui/button.js';
import { Dialog, ConfirmDialog } from '../../components/ui/dialog.js';
import { Input, Select } from '../../components/ui/field.js';
import { Badge, Code, EmptyState } from '../../components/ui/primitives.js';
import { createJourney, isNetworkError, listJourneys, updateJourney } from '../../api/client.js';
import { useCan } from '../../auth/AuthProvider.js';
import { JourneyTimeline } from './JourneyTimeline.js';
import { NewJourneyDialog } from './NewJourneyDialog.js';

const STATUS_TONE: Record<JourneyStatus, 'neutral' | 'warning' | 'success' | 'danger'> = {
  PLANNED: 'neutral',
  IN_PROGRESS: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

/**
 * Where a tool is, in one line.
 *
 * "In transit" on its own is the answer nobody can act on. Between two places
 * it names both; standing somewhere it names that and what it is waiting to do
 * next.
 */
export function whereIs(j: JourneyRow): string {
  if (j.status === 'CANCELLED') return 'Journey cancelled';
  if (j.status === 'COMPLETED') return `Arrived at ${j.at?.name ?? j.destination?.name ?? 'destination'}`;
  if (j.inTransit) {
    const from = j.stops.filter((s) => s.state === 'DEPARTED').at(-1);
    const to = j.headedFor;
    if (from && to) return `On the road, ${from.location.name} → ${to.name}`;
    return 'On the road';
  }
  if (j.at) return `At ${j.at.name}`;
  if (j.status === 'PLANNED') return `Planned, to leave ${j.headedFor?.name ?? 'the start'}`;
  return 'Not yet started';
}

export function JourneysPage() {
  const canWrite = useCan('create', 'journeys');
  const [rows, setRows] = useState<JourneyRow[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [onlyMoving, setOnlyMoving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [open, setOpen] = useState<JourneyRow | null>(null);
  const [confirm, setConfirm] = useState<JourneyRow | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRows(await listJourneys());
    } catch (e) {
      setRows([]);
      setError(
        isNetworkError(e)
          ? 'No connection. Journeys can only be seen online.'
          : 'Could not load journeys.',
      );
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    if (!rows) return rows;
    const needle = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (status && r.status !== status) return false;
      if (onlyMoving && !r.inTransit) return false;
      if (!needle) return true;
      const places = r.stops.map((s) => `${s.location.code} ${s.location.name}`).join(' ');
      return `${r.mould.assetCode} ${r.mould.name} ${places}`.toLowerCase().includes(needle);
    });
  }, [rows, search, status, onlyMoving]);

  const moving = useMemo(() => (rows ?? []).filter((r) => r.inTransit).length, [rows]);

  async function act(row: JourneyRow, action: 'start' | 'cancel') {
    setBusy(true);
    setError(null);
    try {
      await updateJourney(row.id, action);
      await load();
      setConfirm(null);
      setOpen(null);
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'That action could not be applied.');
    } finally {
      setBusy(false);
    }
  }

  const columns: Array<Column<JourneyRow>> = [
    {
      key: 'mould',
      header: 'Mould',
      primary: true,
      sortValue: (r) => r.mould.assetCode,
      render: (r) => (
        <span className="flex min-w-0 items-center gap-2">
          <Code className="font-semibold">{r.mould.assetCode}</Code>
          <span className="truncate text-muted-foreground">{r.mould.name}</span>
        </span>
      ),
    },
    {
      key: 'trip',
      header: 'Trip',
      hideOnMobile: true,
      render: (r) => {
        const start = r.stops[0]?.location;
        return (
          <span className="flex items-center gap-1.5 whitespace-nowrap">
            {start?.code ?? '-'}
            <ArrowRight className="size-3.5 text-muted-foreground" aria-hidden />
            {r.destination?.code ?? '-'}
            {r.stops.length > 2 ? (
              <span className="text-xs text-muted-foreground">
                · {r.stops.length - 2} stop{r.stops.length - 2 === 1 ? '' : 's'}
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'where',
      header: 'Where it is',
      sortValue: (r) => whereIs(r),
      render: (r) => (
        <span className="flex min-w-0 items-center gap-1.5">
          {r.inTransit ? (
            <Truck className="size-3.5 shrink-0 text-warning" aria-hidden />
          ) : (
            <MapPin className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          )}
          <span className="truncate">{whereIs(r)}</span>
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortValue: (r) => r.status,
      render: (r) => <Badge tone={STATUS_TONE[r.status]}>{JOURNEY_STATUS_LABEL[r.status]}</Badge>,
    },
    {
      key: 'started',
      header: 'Started',
      hideOnMobile: true,
      sortValue: (r) => r.startedAt ?? '',
      render: (r) =>
        r.startedAt ? (
          new Date(r.startedAt).toLocaleDateString()
        ) : (
          <span className="text-subtle-foreground">not yet</span>
        ),
    },
  ];

  return (
    <Page
      title="Journeys"
      description="Where each mould on the move has got to, and where it is going next."
      actions={
        canWrite ? (
          <Button variant="primary" onClick={() => setCreating(true)}>
            <Plus className="size-4" /> Plan a journey
          </Button>
        ) : null
      }
      toolbar={
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative flex-1">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by mould or place…"
              className="pl-9"
            />
          </div>
          <Select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="sm:w-44"
            placeholder="Any status"
            options={(Object.keys(JOURNEY_STATUS_LABEL) as JourneyStatus[]).map((s) => ({
              value: s,
              label: JOURNEY_STATUS_LABEL[s],
            }))}
          />
          {/* The one filter worth a button of its own: "what is on the road
              right now" is the question this screen gets opened for. */}
          <Button
            variant={onlyMoving ? 'primary' : 'secondary'}
            onClick={() => setOnlyMoving((v) => !v)}
            aria-pressed={onlyMoving}
          >
            <Truck className="size-4" /> On the road
            {moving > 0 ? <span className="ml-1 tabular-nums">({moving})</span> : null}
          </Button>
        </div>
      }
    >
      {error ? (
        <p role="alert" className="mb-3 rounded-lg bg-danger-subtle px-3 py-2 text-sm text-danger">
          {error}
        </p>
      ) : null}

      <DataTable
        rows={filtered}
        columns={columns}
        rowKey={(r) => r.id}
        onRowClick={(r) => setOpen(r)}
        empty={
          <EmptyState
            icon={<RouteIcon className="size-6" aria-hidden />}
            title={onlyMoving ? 'Nothing on the road' : 'No journeys yet'}
            description={
              onlyMoving
                ? 'Every tool on a journey is standing somewhere.'
                : 'A journey records the stops a mould passes between two sites, and fills itself in as it is scanned along the way.'
            }
          />
        }
      />

      {open ? (
        <Dialog
          open
          onOpenChange={(next) => {
            if (!next) setOpen(null);
          }}
          title={`${open.mould.assetCode} - ${open.mould.name}`}
          description={whereIs(open)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setOpen(null)}>
                Close
              </Button>
              {canWrite && open.status === 'PLANNED' ? (
                <Button onClick={() => void act(open, 'start')} loading={busy}>
                  Start it
                </Button>
              ) : null}
              {canWrite && (open.status === 'PLANNED' || open.status === 'IN_PROGRESS') ? (
                <Button variant="danger" onClick={() => setConfirm(open)}>
                  <X className="size-4" /> Cancel journey
                </Button>
              ) : null}
            </>
          }
        >
          <JourneyTimeline journey={open} />
          {open.route ? (
            <p className="mt-3 text-xs text-muted-foreground">
              Planned from route <Code>{open.route.code}</Code> - {open.route.name}
            </p>
          ) : null}
        </Dialog>
      ) : null}

      {creating ? (
        <NewJourneyDialog
          onClose={() => setCreating(false)}
          onCreate={async (input) => {
            await createJourney(input);
            await load();
          }}
        />
      ) : null}

      {confirm ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setConfirm(null);
          }}
          title="Cancel this journey?"
          // Cancelling does not undo the scans: they are a record of what
          // happened, and what happened does not stop having happened.
          description={`${confirm.mould.assetCode} will stop being tracked against this trip. The scans already recorded stay where they are.`}
          confirmLabel="Cancel journey"
          destructive
          loading={busy}
          onConfirm={() => void act(confirm, 'cancel')}
        />
      ) : null}
    </Page>
  );
}
